import Layout from "@/components/Layout/Layout"
import { Card, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"

import { LoadingScreen } from "@/components/ui/loading-spinner"
import { TournamentCard } from "@/components/TournamentCard"
import { useTournamentList } from "@/graphql/hooks/useTournamentList"
import { useAllGamesWithSeasons } from "@/graphql/hooks/useGames"
import { useUrlPagination } from "@/hooks/useUrlPagination"
import { useState, useEffect, useMemo } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Search, Filter, ActivitySquareIcon, Plus, Sparkles, SlidersHorizontal } from "lucide-react"
import { RenderSimplePagination } from "./Tiendas"
import { Badge } from "@/components/ui/badge"
import { useTournamentTypesListPlain } from "@/graphql/hooks/useTournamentTypesList"
import { useCountries, useRegions } from "@/graphql/hooks/Locations/useLocations"
import { useAuthContext } from "@/contexts/AuthContext"

const Eventos = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentPage, setPage } = useUrlPagination()
  const { isAuthenticated, person, loading: loadingUser, isAdmin } = useAuthContext()

  // Filtros iniciales desde la URL
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    gameId: parseInt(searchParams.get("gameId") || "0"),
    seasonId: parseInt(searchParams.get("seasonId") || "0"),
    countryId: parseInt(searchParams.get("countryId") || "0"),
    regionId: parseInt(searchParams.get("regionId") || "0"),
    typeId: parseInt(searchParams.get("typeId") || "0"),
    // isActiveSeason: parseInt(searchParams.get("activeSeason") || "1"),
    // statusId no se maneja por URL/query param a propósito: así no queda
    // expuesto como filtro directamente recorrible por scrapers.
    statusId: [] as number[],
  })

  // Debounce búsqueda
  const [searchTerm, setSearchTerm] = useState(filters.search)
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchTerm }))
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Juegos y temporadas
  const { countries } = useCountries()
  const { regions, loading: loadingRegions } = useRegions(filters.countryId)
  const { games, loading: gamesLoading } = useAllGamesWithSeasons(true, {
    isAdmin,
    isAuthenticated,
  })
  const { types, loading: typesLoading } = useTournamentTypesListPlain()

  const allSeasons = useMemo(() => {
    const seasons = games
      .filter((g) => g.id === filters.gameId)
      .flatMap((g) => {
        return g.Seasons || []
      })
    return seasons
      .filter((s, i, self) => i === self.findIndex((ss) => ss.id === s.id))
      .sort((a, b) => b.id - a.id)
  }, [games, filters.gameId])

  // Lista de torneos
  const { tournaments, hasNextPage, loading, error } = useTournamentList({
    input: {
      seasonId: filters.seasonId,
      gameId: filters.gameId,
      page: currentPage,
      limit: 21,
      search: filters.search,
      countryId: filters.countryId,
      regionId: filters.regionId,
      typeId: filters.typeId,
      statusId: filters.statusId,
      // isActiveSeason: filters.isActiveSeason,
      date: "DESC",
    },
  })

  // Actualiza URL con filtros (sin page)
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams)

    if (filters.search) newParams.set("search", filters.search)
    else newParams.delete("search")

    if (filters.gameId) {
      newParams.set("gameId", filters.gameId.toString())
    } else {
      newParams.delete("gameId")
    }

    if (filters.seasonId) newParams.set("seasonId", filters.seasonId.toString())
    else newParams.delete("seasonId")

    if (filters.countryId) newParams.set("countryId", filters.countryId.toString())
    else newParams.delete("countryId")

    if (filters.regionId) newParams.set("regionId", filters.regionId.toString())
    else newParams.delete("regionId")

    if (filters.typeId) newParams.set("typeId", filters.typeId.toString())
    else newParams.delete("typeId")

    // if (filters.isActiveSeason) newParams.set("activeSeason", filters.isActiveSeason.toString());
    // else newParams.delete("activeSeason");

    // 🚫 no tocamos "page" (lo maneja useUrlPagination) ni "statusId"
    // (se mantiene solo como estado interno, ver comentario más arriba)
    newParams.delete("statusId")
    setSearchParams(newParams)
  }, [filters, searchParams, setSearchParams])

  // Helpers
  const updateFilter = (key: string, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1) // reset al cambiar filtros
  }

  const clearFilters = () => {
    setFilters({
      search: "",
      gameId: 0,
      seasonId: 0,
      countryId: 0,
      regionId: 0,
      typeId: 0,
      statusId: [],
      // isActiveSeason: 1
    })
    setSearchTerm("")
    setPage(1)
  }

  const statusOptions = [
    { value: 1, label: "Programado" },
    { value: 2, label: "Inscripciones" },
    { value: 3, label: "Iniciado" },
    { value: 4, label: "Proceso" },
    { value: 5, label: "Terminado" },
    { value: 6, label: "Reportado" },
  ]

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setFilters((prev) => ({ ...prev, search: searchTerm }))
    }
  }

  // Filtros aplicados (sin contar la búsqueda), para el badge del botón de
  // filtros en mobile.
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false)
  const activeFilterCount =
    [filters.gameId, filters.seasonId, filters.countryId, filters.regionId, filters.typeId].filter(
      Boolean,
    ).length + (filters.statusId.length > 0 ? 1 : 0)

  // Campos de filtro (sin el wrapper de grilla) para reutilizarlos tanto en
  // el layout de escritorio como dentro del Sheet de mobile.
  const renderFilterFields = () => (
    <>
      {/* Game Filter */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Formato
        </label>
        <Select
          value={filters.gameId.toString()}
          onValueChange={(value) => {
            updateFilter("gameId", parseInt(value))
            updateFilter("seasonId", 0)
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Todos los formatos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todos los formatos</SelectItem>
            {games.map((game) => (
              <SelectItem
                key={game.id}
                value={game.id.toString()}
                className={`${game.isActive ? "" : "text-muted-foreground"} cursor-pointer`}
              >
                {game.isActive === 1 && <ActivitySquareIcon className="inline h-4 w-4 mr-2" />}
                {game.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Type Filter */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Tipo de torneo
        </label>
        <Select
          value={filters.typeId.toString()}
          onValueChange={(value) => updateFilter("typeId", parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todos los tipos</SelectItem>
            {types?.map((type) => (
              <SelectItem key={type?.id} value={type?.id.toString()}>
                {type?.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Season Filter */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Temporada
        </label>
        <Select
          value={filters.seasonId.toString()}
          onValueChange={(value) => updateFilter("seasonId", parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Todas las temporadas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todas las temporadas</SelectItem>
            {allSeasons.map((season) => (
              <SelectItem key={season.id} value={season.id.toString()} className="cursor-pointer">
                {season.isActive === 1 && (
                  <Badge variant="secondary" className="h-4 inline mr-4">
                    Activa
                  </Badge>
                )}
                {season.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Country Filter */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          País
        </label>
        <Select
          value={filters.countryId.toString()}
          onValueChange={(value) => {
            updateFilter("countryId", parseInt(value))
            updateFilter("regionId", 0)
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Todos los países" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todos los países</SelectItem>
            {countries?.map((country) => (
              <SelectItem key={country?.id} value={country?.id.toString()}>
                {country?.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Region Filter */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Región
        </label>
        <Select
          value={filters.regionId.toString()}
          onValueChange={(value) => updateFilter("regionId", parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Todas las regiones" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Todas las regiones</SelectItem>
            {regions.map((region) => (
              <SelectItem key={region?.id} value={region?.id?.toString()}>
                {region?.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* MultiSelect Estado */}
      <MultiSelectEstado statusOptions={statusOptions} filters={filters} setFilters={setFilters} />
    </>
  )

  if (gamesLoading || typesLoading || loading || loadingRegions) {
    return <LoadingScreen message="Estamos buscando los eventos..." />
  }

  const canCreateTournament = isAuthenticated && !loadingUser && person?.Store?.active === 1

  return (
    <Layout>
      <div className="space-y-4 py-4 px-2 md:px-0">
        {/* Header */}
        <section className="relative overflow-hidden rounded-2xl border border-primary/15 bg-card/55 px-5 py-6 shadow-[0_10px_45px_rgba(2,6,23,0.08)] backdrop-blur-xl md:px-8 md:py-8">
          <div className="absolute -left-20 -top-14 h-44 w-44 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -right-20 top-10 h-56 w-56 rounded-full bg-cyan-500/10 blur-3xl" />

          <div className="relative flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2">
              <Badge className="inline-flex gap-2 border-primary/25 bg-primary/10 px-3 py-1 text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Torneos Oficiales MyL
              </Badge>

              <h1 className="text-3xl font-black tracking-tight md:text-5xl">
                Torneos
                <span className="block bg-gradient-to-r from-primary via-cyan-400 to-primary bg-[length:200%_200%] bg-clip-text text-transparent animate-gradient">
                  y Eventos
                </span>
              </h1>

              <p className="max-w-3xl text-base text-muted-foreground md:text-lg">
                Encuentra los mejores torneos y competencias de la comunidad
              </p>
            </div>

            {canCreateTournament && (
              <Link to="/tournaments/create" className="shrink-0">
                <Button
                  variant="default"
                  size="lg"
                  className="flex items-center gap-2 rounded-xl shadow-md"
                >
                  <Plus className="w-4 h-4" />
                  Crear Torneo
                </Button>
              </Link>
            )}
          </div>
        </section>

        {/* Filters */}
        <div className="rounded-2xl border border-border/60 bg-card p-5 md:p-6 space-y-5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-foreground">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
                Filtros
              </span>
            </div>

            {/* Mobile: abre el resto de los filtros en un sidebar */}
            <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 md:hidden">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filtros
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="h-5 px-1.5">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-5/6 overflow-y-auto sm:max-w-sm">
                <SheetHeader>
                  <SheetTitle>Filtros</SheetTitle>
                </SheetHeader>
                <div className="grid grid-cols-1 gap-4 mt-4">{renderFilterFields()}</div>
                <div className="flex justify-between gap-2 pt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Limpiar filtros
                  </Button>
                  <Button size="sm" onClick={() => setIsFilterSheetOpen(false)}>
                    Ver resultados
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" />
            <Input
              placeholder="Buscar torneos por nombre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pl-10"
            />
          </div>

          {/* Filter Grid - solo en desktop, en mobile va en el Sheet de arriba */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderFilterFields()}
          </div>

          {/* Clear Filters - solo desktop */}
          <div className="hidden md:flex justify-end pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground hover:text-foreground"
            >
              Limpiar filtros
            </Button>
          </div>
        </div>

        {/* Resultados */}
        {loading ? (
          <LoadingScreen message="Buscando torneos..." />
        ) : error ? (
          <div>
            <div className="text-center py-12">
              <p className="text-destructive">Error al cargar los torneos</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-muted rounded-t-lg"></div>
                  <CardHeader>
                    <div className="h-6 bg-muted rounded w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-full"></div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        ) : tournaments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              No se encontraron torneos con los filtros aplicados
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {tournaments.map((t) => (
                <TournamentCard key={t.id} tournament={t} />
              ))}
            </div>

            <RenderSimplePagination
              hasNextPage={hasNextPage}
              currentPage={currentPage}
              setPage={setPage}
            />
          </>
        )}
      </div>
    </Layout>
  )
}

export default Eventos

export const MultiSelectEstado = ({ statusOptions, filters, setFilters }) => {
  const [selected, setSelected] = useState(filters.statusId || [])

  const toggleValue = (value: string) => {
    setSelected((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value)
      } else {
        return [...prev, Number(value)]
      }
    })
  }

  // sincronizas con el padre
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      statusId: selected.length === 0 ? [] : selected,
    }))
  }, [selected, setFilters])

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Estado del evento
      </label>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full justify-between font-normal">
            {selected.length > 0 ? `${selected.length} seleccionado(s)` : "Todos los estados"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          {statusOptions.map((status) => {
            const isChecked = selected.includes(status.value)
            return (
              <DropdownMenuCheckboxItem
                key={status.value}
                checked={isChecked}
                onCheckedChange={() => toggleValue(status.value)}
              >
                {status.label}
              </DropdownMenuCheckboxItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
